import { FC, useRef, useEffect } from 'react';
import Link from 'next/link';
import type { Icon } from '@phosphor-icons/react';
import s from './TabText.module.scss';

type Props = {
  label: string;
  selected: boolean;
  icon?: Icon;
  iconColor?: string;
} & (
  | {
      href: string;
      onClick?: never;
    }
  | {
      href?: never;
      onClick: () => unknown;
    }
);

const TabText: FC<Props> = ({ label, icon: IconComponent, iconColor, selected, href, onClick }) => {
  const tabRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const currentRef = href ? anchorRef.current : tabRef.current;
    if (selected && currentRef) {
      currentRef.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [selected, href]);

  const sharedProps = {
    className: s.container,
    style: selected ? { background: 'var(--color-gray-25)' } : {},
  };

  return href
    ? (
      <Link
        ref={anchorRef}
        prefetch={false}
        href={href}
        {...sharedProps}
      >
        {IconComponent && (
          <IconComponent
            size={12}
            weight="fill"
            color={iconColor || 'var(--color-primary)'}
          />
        )}
        {label}
      </Link>
    )
    : (
      <button
        ref={tabRef}
        onClick={onClick}
        {...sharedProps}
      >
        {IconComponent && (
          <IconComponent
            size={12}
            weight="fill"
            color={iconColor || 'var(--color-primary)'}
          />
        )}
        {label}
      </button>
    );
};

export default TabText;
